import { Fragment } from "react";
import { Helmet } from "react-helmet-async";
import {
  HiOutlineBuildingLibrary,
  HiOutlineChartBar,
  HiOutlineUserGroup,
  HiOutlineGlobeAlt,
} from "react-icons/hi2";

import s from "./StaticPage.module.css";
import premier from "../../images/premier.jpeg";
import { bankName } from "../../util/config";

const Banking = () => {
  return (
    <Fragment>
      <Helmet>
        <title>Banking — {bankName}</title>
        <meta
          name="description"
          content={`Premier banking, dedicated relationship managers, and digital tools from ${bankName}.`}
        />
        <link rel="canonical" href="/banking" />
      </Helmet>
      <section className={s.page}>
        <div
          className={s.hero}
          style={{ backgroundImage: `url(${premier})` }}
        >
          <div className={s.heroOverlay} />
          <div className={s.heroContent}>
            <p className={s.eyebrow}>Private &amp; premier banking</p>
            <h1>Premier banking, crafted around your life</h1>
            <p className={s.lead}>
              A relationship-led current account with no monthly fee, concierge
              support, and seamless digital access—so you can manage wealth,
              family, and everyday money in one trusted place.
            </p>
          </div>
        </div>

        <div className={s.metrics}>
          <div className={s.metric}>
            <span className={s.metricValue}>24/7</span>
            <span className={s.metricLabel}>Secure online &amp; mobile access</span>
          </div>
          <div className={s.metric}>
            <span className={s.metricValue}>FINMA</span>
            <span className={s.metricLabel}>Regulated services &amp; clear fees</span>
          </div>
          <div className={s.metric}>
            <span className={s.metricValue}>Dedicated</span>
            <span className={s.metricLabel}>Premier relationship support</span>
          </div>
        </div>

        <div className={s.section}>
          <div className={s.sectionHeader}>
            <h2>Why clients choose {bankName} Premier</h2>
            <p className={s.sectionIntro}>
              From milestone purchases to long-term planning, we combine human
              expertise with robust digital tools—always with transparency on
              eligibility, charges, and how your data is protected.
            </p>
          </div>
          <div className={s.grid}>
            <div className={s.card}>
              <div className={s.cardIcon}>
                <HiOutlineBuildingLibrary aria-hidden />
              </div>
              <h3>Concierge-style service</h3>
              <p>
                Experienced bankers help you navigate day-to-day banking,
                international payments, and larger decisions—with clear
                timelines and documentation you can revisit anytime.
              </p>
            </div>
            <div className={s.card}>
              <div className={s.cardIcon}>
                <HiOutlineChartBar aria-hidden />
              </div>
              <h3>Planning &amp; investment pathways</h3>
              <p>
                Access tailored conversations about savings, investments, and
                protection. Where advice is offered, we explain costs upfront
                and only proceed when the service is right for you.
              </p>
            </div>
            <div className={s.card}>
              <div className={s.cardIcon}>
                <HiOutlineGlobeAlt aria-hidden />
              </div>
              <h3>Global connectivity</h3>
              <p>
                Move money confidently with traceable transfers, competitive
                FX information, and alerts that keep you informed across
                currencies and time zones.
              </p>
            </div>
            <div className={s.card}>
              <div className={s.cardIcon}>
                <HiOutlineUserGroup aria-hidden />
              </div>
              <h3>Family-aware banking</h3>
              <p>
                Structures that reflect how households really operate—joint
                visibility where appropriate, and guidance when life changes
                (education, relocation, care, inheritance).
              </p>
            </div>
          </div>
        </div>

        <div className={s.split}>
          <div className={s.splitText}>
            <h2>Arranged overdrafts, explained with clarity</h2>
            <p>
              When you open this account, you may apply for an arranged
              overdraft—a pre-agreed limit for short-term liquidity. We show
              interest and fees before you borrow, and you can change your limit
              through online banking, our app, by phone, or in branch.
            </p>
            <p>
              If a payment would exceed your limit, we may attempt to honour
              essential payments where regulation allows; unarranged use can
              affect your credit file. Overdrafts are for short-term needs, not
              long-term debt—our team can discuss alternatives if borrowing
              becomes persistent.
            </p>
          </div>
          <div className={s.splitText}>
            <div className={s.highlight}>
              <p>
                <strong>Trust &amp; security.</strong> We monitor unusual
                activity, use strong authentication for high-risk actions, and
                never ask for your full password or PIN by email. Report
                anything suspicious immediately—we treat it as priority.
              </p>
            </div>
          </div>
        </div>

        <p className={s.trustStrip}>
          <strong>{bankName}</strong> is authorised and regulated by the Swiss
          Financial Market Supervisory Authority (FINMA). Eligibility criteria,
          product terms, and fees apply; always read the pre-contractual
          information before you apply.
        </p>
      </section>
    </Fragment>
  );
};

export default Banking;
